// Public legal pages: /legal/terms, /legal/privacy, /legal/vendor-terms, /legal/refunds,
// /legal/notices — the latest version, with the company's details filled in.
import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Box, Container, Paper, Typography, Stack, Chip, Button, Alert, CircularProgress, Link } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LEGAL_DOCUMENT_QUERY, LEGAL_DOCUMENTS_QUERY } from '../graphql/queries';
import MarkdownView from '../components/MarkdownView';
import LegalFooter from '../components/LegalFooter';

// URL slug ↔ document key
export const LEGAL_PAGES = [
  { slug: 'terms', key: 'terms', label: 'Terms & Conditions' },
  { slug: 'privacy', key: 'privacy', label: 'Privacy Policy' },
  { slug: 'refunds', key: 'refunds', label: 'Refunds & Cancellations' },
  { slug: 'vendor-terms', key: 'vendor_terms', label: 'Vendor Agreement' },
  { slug: 'notices', key: 'notices', label: 'Legal Notices & Grievance Officer' },
];
const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

export default function LegalPage() {
  const { slug } = useParams();
  const page = LEGAL_PAGES.find(p => p.slug === slug) || LEGAL_PAGES[0];
  const { data, loading, error } = useQuery(LEGAL_DOCUMENT_QUERY, { variables: { key: page.key } });
  const { data: listData } = useQuery(LEGAL_DOCUMENTS_QUERY);
  const doc = data?.legalDocument;
  const available = new Set((listData?.legalDocuments || []).map(d => d.key));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fdf6ff', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 }, flex: 1 }}>
        <Button component={RouterLink} to="/" startIcon={<ArrowBackIcon />} size="small" sx={{ mb: 1, '@media print': { display: 'none' } }}>Home</Button>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2} sx={{ '@media print': { display: 'none' } }}>
          {LEGAL_PAGES.filter(p => !listData || available.has(p.key)).map(p => (
            <Chip key={p.slug} component={RouterLink} to={`/legal/${p.slug}`} clickable label={p.label}
              color={p.slug === page.slug ? 'primary' : 'default'} variant={p.slug === page.slug ? 'filled' : 'outlined'} />
          ))}
        </Stack>
        <Paper elevation={2} sx={{ p: { xs: 2.5, sm: 5 }, borderRadius: 3 }}>
          {loading && <Stack alignItems="center" py={6}><CircularProgress /></Stack>}
          {error && <Alert severity="error">{error.message}</Alert>}
          {!loading && !doc && !error && <Alert severity="info">This document is not available.</Alert>}
          {doc && (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} mb={1}>
                <Typography variant="h4" fontWeight={700}>{doc.title}</Typography>
                <Button size="small" startIcon={<PrintIcon />} onClick={() => window.print()} sx={{ alignSelf: 'flex-start', '@media print': { display: 'none' } }}>Print / Save as PDF</Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Effective from {fmt(doc.effectiveDate)} · Version {doc.version}
              </Typography>
              {doc.summary && (
                <Alert severity="info" icon={false} sx={{ mb: 3 }}>
                  <Typography variant="body2"><b>In short:</b> {doc.summary}</Typography>
                </Alert>
              )}
              <MarkdownView text={doc.body} />
              <Typography variant="caption" color="text.secondary" display="block" mt={4}>
                Questions or a complaint? <Link component={RouterLink} to="/legal/grievance">Contact our Grievance Officer</Link> · Your data rights: <Link component={RouterLink} to="/dashboard/privacy">My data & privacy</Link> (sign in) · <Link component={RouterLink} to="/legal/notices">Legal Notices</Link>.
              </Typography>
            </>
          )}
        </Paper>
      </Container>
      <LegalFooter />
    </Box>
  );
}
